import styles from './creditPaymentForm.module.css'
import { useMemo, useState } from "react"
import { FieldType, FormField, ModalId, StaticValue, useUIActions, ValidationModal2 } from "../../../../../features/ui"
import { Data2, DataTypeForm2, useDataTypesHooks, useDescribeHooks } from "../../../../../features/data-types"
import { useNavigate } from "react-router-dom"
import { Customer, SalePaymentItemData, SalePaymentItemsTable, SalePaymentItemFormModal } from "../../../../sales"
import { CustomerDebtData } from "../../../hooks/types"
import { usePricesHooks } from "../../../../operations"
import { useActions } from './actions'
import { usePaymentTypeLoaders } from '../../../loaders/paymentTypeLoaders'
import { firstItem, toList } from '../../../../../utils/listUtils'
import { PaymentCategory } from '../../../state/types'
import { isSelectablePaymentType } from '../../../utils/paymentTypeUtils'

type Props = {
    customer: Customer
    debtData: CustomerDebtData
    reload: () => void
}

export const CreditPaymentForm = (props: Props) => {
    const { customer, debtData, reload } = props
    const { creditInFavor = 0 } = customer
    
    const [salePaymentItems, setSalePaymentItems] = useState<SalePaymentItemData[]>([])
    const [currentSalePaymentItem, setCurrentSalePaymentItem] = useState<SalePaymentItemData>()
    const [debtChanged, setDebtChanged] = useState<boolean>(false)

    const pricesHooks = usePricesHooks()
    const { describe } = useDescribeHooks()
    const { formatDate, formatPrice } = useDataTypesHooks()
    const navigate = useNavigate()
    
    const paymentTypeLoaders = usePaymentTypeLoaders()

    const { validateDebt, submitCreditPayment } = useActions()
    const { openModal } = useUIActions()

    const itemPaymentType = useMemo(() => paymentTypeLoaders().findPaymentType(firstItem(salePaymentItems)?.paymentTypeId), [salePaymentItems])

    const debt = useMemo(() => debtData?.debt, [debtData])
    // With debt covered by the credit in favor the payment is made with the credit alone. Without debt it's a payment
    // on account: a payment type is needed
    const creditCoversDebt = useMemo(() => debt > 0 && creditInFavor >= debt, [creditInFavor, debt])
    const adjustedDebt = useMemo(() => pricesHooks.adjustment.apply(debt - creditInFavor, itemPaymentType?.yield || 0), [debt, creditInFavor, itemPaymentType])

    const paymentAmount = useMemo(() => salePaymentItems.reduce((sum, salePaymentItem) => sum + salePaymentItem.amount, 0), [salePaymentItems])
    const adjustedPaymentAmount = useMemo(() => pricesHooks.adjustment.remove(paymentAmount, itemPaymentType?.yield || 0), [paymentAmount, itemPaymentType])
    const salePayedAmount = useMemo(() => Math.min(adjustedPaymentAmount, debt - creditInFavor), [adjustedPaymentAmount, debt, creditInFavor])
    
    const newDebt = useMemo(() => Math.max(debt - creditInFavor - salePayedAmount, 0), [debt, creditInFavor, salePayedAmount])
    const newCredit = useMemo(() => Math.max(adjustedPaymentAmount - salePayedAmount, 0), [adjustedPaymentAmount, salePayedAmount])

    const paymentTypes = useMemo(() => {
        return paymentTypeLoaders().findAllPaymentTypes(true).filter(paymentType =>
            isSelectablePaymentType(paymentType, currentSalePaymentItem?.paymentTypeId) &&
            (paymentType.category === PaymentCategory.CHECK || paymentType.cashFundIds.length > 0) &&
            (currentSalePaymentItem?.paymentTypeId === paymentType.id || !salePaymentItems.map(salePaymentItem => salePaymentItem.paymentTypeId).includes(paymentType.id))
        )
    }, [salePaymentItems, currentSalePaymentItem])

    const defaultAmounts = useMemo(() => {
        const pendingAmountsMap = new Map<string, number>()
        paymentTypes.forEach(paymentType => pendingAmountsMap.set(paymentType.id, Math.max(0, pricesHooks.adjustment.apply(adjustedDebt, paymentType.yield))))
        return pendingAmountsMap
    }, [paymentTypes, adjustedDebt])

    const sortItems = (items: Partial<SalePaymentItemData>[]) => {
        return items
            .sort((a, b) => a.paymentTypeId!.localeCompare(b.paymentTypeId!))
            .map((salePaymentItem, index) => ({
                ...salePaymentItem,
                index: index.toString()
            })) as SalePaymentItemData[]
    }

    const onAddPaymentItem = () => {
        setCurrentSalePaymentItem(undefined)
        openModal(ModalId.SALE_PAYMENT_ITEM_FORM)
    }

    const onEditPaymentItem = (index: string) => {
        setCurrentSalePaymentItem(salePaymentItems.find((_, i) => i.toString() === index))
        openModal(ModalId.SALE_PAYMENT_ITEM_FORM)
    }

    const onRemovePaymentItem = (index: string) => {
        const newSalePaymentItems = salePaymentItems.filter((_, i) => i.toString() !== index)
        setSalePaymentItems(sortItems(newSalePaymentItems))
    }

    const fields: FormField[] = [
        {
            name: 'customer',
            type: FieldType.TEXT,
            label: 'Cliente',
            loading: !customer,
            render: () => <>{customer!.name}</>
        },
        {
            name: 'operations',
            type: FieldType.TABLE,
            label: 'Deudas Pendientes',
            loading: !debtData,
            render: () => (
                <div className={styles.container}>
                    {debtData!.operationsData.map((operationData, index) => (
                        <div key={index} className={styles.dataContainer}>
                            <span>{describe(operationData.table, operationData.operation)} - {formatDate(new Date(operationData.operation.creationDate))}:</span>
                            <b>{formatPrice(operationData.debt)}</b>
                        </div>
                    ))}
                </div>
            )
        },
        {
            name: 'debt',
            type: FieldType.PRICE,
            label: 'Deuda Total',
            loading: debt === undefined,
            render: () => <StaticValue type={FieldType.PRICE} value={debt} />
        },
        {
            name: 'paymentDate',
            type: FieldType.DATE,
            label: 'Fecha de Cobro',
            defaultValue: new Date(),
            max: new Date()
        },
        {
            name: 'salePaymentItems',
            type: FieldType.TABLE,
            label: 'Medios de Pago',
            render: () => (
                <SalePaymentItemsTable
                    salePaymentItems={salePaymentItems}
                    disableCreate={creditCoversDebt || salePaymentItems.length > 0}
                    createTooltip={creditCoversDebt ? ['El pago está cubierto', 'por el crédito a favor'] : undefined}
                    onCreate={onAddPaymentItem}
                    onEditRow={onEditPaymentItem}
                    onRemoveRow={onRemovePaymentItem}
                    createMode
                />
            )
        },
        {
            name: 'newDebtOrCredit',
            type: FieldType.PRICE,
            label: newCredit > 0 ? 'Crédito a Favor' : 'Saldo Pendiente',
            render: () => <StaticValue type={FieldType.PRICE} value={Math.abs(newDebt || newCredit)} />
        }
    ]
    if (creditInFavor > 0) {
        const creditField = {
            name: 'credit',
            type: FieldType.PRICE,
            label: 'Crédito a Favor',
            render: () => <StaticValue type={FieldType.PRICE} value={creditInFavor} />
        }
        fields.splice(3, 0, creditField)
    }
    const validate = async (): Promise<string[]> => {
        const validations = !creditCoversDebt && salePaymentItems.length === 0 ? toList('Debe agregar al menos 1 medio de pago') : []
        if (validations.length === 0) {
            const debtValidation = await validateDebt(customer!.id, debtData.debt)
            setDebtChanged(!!debtValidation)
            debtValidation && validations.push(debtValidation)
        }
        return validations
    }

    const onSubmit = async (data: Data2) => {
        await submitCreditPayment(customer!.id, data.paymentDate! as Date, firstItem(salePaymentItems)!)
    }

    const onCancel = () => navigate(-1)

    const onSaveSalePaymentItem = (newPartialItem: Partial<SalePaymentItemData>) => {
        const newPartialSalePaymentItems = currentSalePaymentItem ?
            salePaymentItems.map(salePaymentItem => salePaymentItem.index === currentSalePaymentItem.index ? newPartialItem : salePaymentItem) :
            [newPartialItem, ...salePaymentItems]
        const newSalePaymentItems = sortItems(newPartialSalePaymentItems)
        setSalePaymentItems(newSalePaymentItems)
    }

    return (
        <>
            <DataTypeForm2
                formId="credit-payment-form"
                fields={fields}
                createLabel='Confirmar'
                onSubmit={onSubmit}
                onCancel={onCancel}
                validate={validate}
            />
            <SalePaymentItemFormModal
                salePaymentItem={currentSalePaymentItem}
                paymentTypes={paymentTypes}
                defaultAmounts={defaultAmounts}
                hideAdjustments
                onSubmit={onSaveSalePaymentItem}
            />
            <ValidationModal2
                title="Validaciones"
                submitLabel={debtChanged ? "Refrescar Deuda" : undefined}
                closeable={!debtChanged}
                onSubmit={debtChanged ? reload : undefined}
            />
        </>
    )
}
